import React, { useState } from "react";
import logo from "../assets/images/logo/Header-logo.png";

// ============================================================================
// DATA & CONSTANTS
// ============================================================================

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
];

const TRAILING_LINKS = [
  { label: "Team", href: "/team" },
  { label: "Blog", href: "/blogs" },
  { label: "Contact Us", href: "/contact-us" },
];

// 8 categories, each with its own sub-dropdown
// 8 core categories, each populated with relevant sub-dropdown items
const PRACTICE_CATEGORIES = [
  {
    label: "Service Matter",
    children: ["SLP", "PIL", "WRIT PETITION", "CAT", "TP"],
  },
  {
    label: "Criminal Matter",
    children: ["Anticipatory Bail", "CHEQUE BOUNCE", "BAIL", "MURDER", "RAPE", "POSCO"],
  },
  {
    label: "Family Matter",
    children: ["DOMESTIC VIOLENCE", "DOWRY", "DOWRY & DEATH", "CHILD CUSTODY", "MAINENCANCE"],
  },
  {
    label: "Property Matter",
    children: ["DELAY IN POSSESSION", "ENCROCHMENT"],
  },
  {
    label: "Company Matter",
    children: ["NCLT", "NCLAT"],
  },
  {
    label: "Banking Matter",
    children: ["DRT", "DRAT"],
  },
  {
    label: "Intellectual Property",
    children: ["TRADE MARK MATTERS/REGISTRATIONS"],
  },
  {
    label: "Cyber Crime",
    children: ["DCRDC", "SCDRC" ,"NCDRC"],
  },
];


// 12 plain services, no further nesting
const PRACTICE_SERVICES = [
  "RERA",
  "Cyber Crime",
  "NDPS",
  "Tender Litigation",
  "White Collar Crime",
  "Juvenile Matter",
  "Arbitration, Conciliation & ODR",
  "EOW",
  "ED",
  "CBI",
  "PMLA",
  "Trademark Registration",
];

// Utility: hides scrollbar across browsers while keeping scroll functional
const SCROLLBAR_HIDE =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

// ============================================================================
// HELPERS
// ============================================================================

// Utility to create URL-friendly links from strings (e.g. "Civil Suits" -> "civil-suits")
const createSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
    .replace(/(^-|-$)+/g, "");   // Remove leading or trailing hyphens
};

const ChevronIcon = ({ className = "" }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={`shrink-0 transition-transform duration-200 ${className}`}
  >
    <polyline points="2,3 5,7 8,3" />
  </svg>
);

// Top-level link with an animated underline hover effect
const NavAnchor = ({ href, children }) => (
  <a
    href={href}
    className="relative px-[18px] h-[72px] flex items-center text-[11px] font-bold tracking-[0.14em] uppercase text-gray-600 hover:text-black transition-colors whitespace-nowrap after:content-[''] after:absolute after:left-[18px] after:right-[18px] after:bottom-0 after:h-[2px] after:bg-black after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100"
  >
    {children}
  </a>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Header() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  const toggleCategory = (label) => {
    setOpenCategories((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="w-full bg-white font-['Lato',sans-serif] border-b border-gray-200 shadow-sm relative z-50">
      
      {/* ── TOP BAR ── */}
      <div className="hidden md:flex bg-black px-10 py-2 justify-end items-center gap-5">
        <span className="text-[11px] text-gray-300 tracking-[0.08em]">
          <b className="text-white font-bold">Mon–Sat:</b> 10:00 AM – 7:00 PM
        </span>
        <span className="text-gray-700">|</span>
        <span className="text-[11px] text-gray-300 tracking-[0.08em]">
          <b className="text-white font-bold">Email:</b> rai@raiandassociates.in
        </span>
        <span className="text-gray-700">|</span>
        <span className="text-[11px] text-gray-300 tracking-[0.08em]">
          <b className="text-white font-bold"></b> +91 70425 13698  <br/> +91 87682 62710
        </span>
      </div>

      {/* ── MAIN NAV ROW ── */}
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 flex items-center justify-between h-[72px]">
        
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img
            src={logo}
            alt="Rai & Associates"
            className="h-16 w-auto"
          />
        </a>

        <div className="hidden md:block w-px h-9 bg-gray-200 mx-8" />

        {/* ── DESKTOP NAV ── */}
        <nav className="hidden md:flex items-center h-full">
          {NAV_LINKS.map((link) => (
            <NavAnchor key={link.label} href={link.href}>
              {link.label}
            </NavAnchor>
          ))}

          {/* Practice Areas Dropdown */}
          <div className="relative group h-full flex items-center">
            <button
              type="button"
              className="relative flex items-center gap-[5px] px-[18px] h-[72px] text-[11px] font-bold tracking-[0.14em] uppercase transition-colors whitespace-nowrap bg-transparent text-gray-600 group-hover:text-black after:content-[''] after:absolute after:left-[18px] after:right-[18px] after:bottom-0 after:h-[2px] after:bg-black after:scale-x-0 group-hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-300"
            >
              Practice Areas
              <ChevronIcon className="transition-transform duration-200 group-hover:rotate-180" />
            </button>

            {/* Main Dropdown Panel */}
            <div className="absolute top-full left-0 w-[300px] bg-white border border-gray-200 border-t-2 border-t-black shadow-lg hidden group-hover:block z-[999]">
              
              {/* Top Section: Categories */}
              <div className="py-2">
                {PRACTICE_CATEGORIES.map((cat) => (
                  <div key={cat.label} className="relative group/sub">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-5 py-2.5 text-[11px] tracking-[0.1em] uppercase text-left bg-transparent transition-all duration-200 border-l-2 text-gray-600 border-l-transparent group-hover/sub:text-black group-hover/sub:bg-gray-50 group-hover/sub:border-l-black"
                    >
                      {cat.label}
                      <ChevronIcon className="-rotate-90 transition-transform group-hover/sub:translate-x-1" />
                    </button>

                    {/* Sub-dropdown Panel (The individual links) */}
                    <div className={`absolute top-0 left-full w-[260px] max-h-[300px] overflow-y-auto ${SCROLLBAR_HIDE} bg-white border border-gray-200 shadow-lg hidden group-hover/sub:block z-[1000] border-t-2 border-t-black`}>
                      {cat.children.map((child) => (
                        <a
                          key={child}
                          href={`/services/${createSlug(child)}`}
                          className="block px-5 py-3 text-[10px] tracking-[0.08em] uppercase text-gray-500 border-b border-gray-50 hover:text-black hover:pl-7 hover:bg-gray-50 transition-all duration-150 last:border-b-0"
                        >
                          {child}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom Section: More Services */}
              <div className="bg-gray-50 border-t border-gray-100">
                <div className="px-5 pt-4 pb-2 text-[9px] tracking-[0.18em] uppercase text-gray-400">
                  More Services
                </div>
                <div className={`max-h-[220px] overflow-y-auto ${SCROLLBAR_HIDE} pb-2`}>
                  {PRACTICE_SERVICES.map((service) => (
                    <a
                      key={service}
                      href={`/services/${createSlug(service)}`}
                      className="block px-5 py-2.5 text-[11px] tracking-[0.1em] uppercase text-gray-600 border-l-2 border-l-transparent hover:text-black hover:bg-gray-100 hover:border-l-gray-300 hover:pl-[26px] transition-all duration-150"
                    >
                      {service}
                    </a>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {TRAILING_LINKS.map((link) => (
            <NavAnchor key={link.label} href={link.href}>
              {link.label}
            </NavAnchor>
          ))}
        </nav>

        {/* Desktop Consult CTA */}
        <a
          href="/contact-us"
          className="hidden md:inline-block ml-3 px-[22px] py-[9px] bg-transparent border border-black text-black text-[10px] font-bold tracking-[0.18em] uppercase transition-all duration-300 hover:bg-black hover:text-white hover:-translate-y-0.5 hover:shadow-md whitespace-nowrap"
        >
          Consult Now
        </a>

        {/* ── MOBILE HAMBURGER BUTTON ── */}
        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setIsMobileOpen((v) => !v)}
          className="md:hidden text-black p-2"
        >
          {isMobileOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="4" y1="4" x2="20" y2="20" />
              <line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </div>

      {/* ── MOBILE MENU ── */}
      {isMobileOpen && (
        <div className={`md:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-72px)] overflow-y-auto ${SCROLLBAR_HIDE}`}>
          
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="flex justify-between items-center px-6 py-3.5 text-[11px] font-bold tracking-[0.15em] uppercase text-gray-600 border-b border-gray-100 hover:text-black hover:bg-gray-50 transition-colors"
            >
              {link.label}
            </a>
          ))}

          {/* Practice Areas Mobile Toggle */}
          <button
            type="button"
            onClick={() => {
              setIsMobileDropdownOpen((v) => !v);
              if (isMobileDropdownOpen) setOpenCategories({});
            }}
            className={`w-full flex justify-between items-center px-6 py-3.5 text-[11px] font-bold tracking-[0.15em] uppercase border-b border-gray-100 bg-transparent transition-colors ${
              isMobileDropdownOpen ? "text-black bg-gray-50" : "text-gray-600 hover:text-black hover:bg-gray-50"
            }`}
          >
            Practice Areas
            <ChevronIcon className={isMobileDropdownOpen ? "rotate-180" : ""} />
          </button>

          {isMobileDropdownOpen && (
            <div className={`bg-gray-50 ${SCROLLBAR_HIDE}`}>
              {/* Categories */}
              {PRACTICE_CATEGORIES.map((cat) => (
                <div key={cat.label}>
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.label)}
                    className={`w-full flex justify-between items-center py-2.5 pr-6 pl-9 text-[10px] tracking-[0.1em] uppercase border-b border-gray-100 bg-transparent transition-colors ${
                      openCategories[cat.label] ? "text-black bg-gray-100" : "text-gray-500 hover:text-black hover:bg-gray-100"
                    }`}
                  >
                    {cat.label}
                    <ChevronIcon className={openCategories[cat.label] ? "rotate-180" : "-rotate-90"} />
                  </button>
                  
                  {/* Category Children Links */}
                  {openCategories[cat.label] && (
                    <div className="bg-white">
                      {cat.children.map((child) => (
                        <a
                          key={child}
                          href={`/services/${createSlug(child)}`}
                          className="block py-2 pr-6 pl-14 text-[9px] tracking-[0.08em] uppercase text-gray-400 border-b border-gray-100 hover:text-black hover:bg-gray-50"
                        >
                          {child}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* More Services */}
              <div className="pt-3 pb-1 pr-6 pl-9 text-[9px] tracking-[0.18em] uppercase text-gray-400 border-t border-gray-200">
                More Services
              </div>
              {PRACTICE_SERVICES.map((service) => (
                <a
                  key={service}
                  href={`/services/${createSlug(service)}`}
                  className="block py-2.5 pr-6 pl-9 text-[10px] tracking-[0.1em] uppercase text-gray-500 border-b border-gray-100 hover:text-black hover:bg-gray-100 hover:pl-11 transition-all duration-150"
                >
                  {service}
                </a>
              ))}
            </div>
          )}

          {TRAILING_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="flex justify-between items-center px-6 py-3.5 text-[11px] font-bold tracking-[0.15em] uppercase text-gray-600 border-b border-gray-100 hover:text-black hover:bg-gray-50 transition-colors"
            >
              {link.label}
            </a>
          ))}

          {/* Mobile Consult CTA */}
          <a
            href="/contact-us"
            className="block mx-6 mt-4 mb-5 py-3 text-center bg-transparent border border-black text-black text-[10px] font-bold tracking-[0.18em] uppercase transition-all duration-300 hover:bg-black hover:text-white"
          >
            Consult Now
          </a>
        </div>
      )}
    </div>
  );
}